import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, Space, Tag, message, Popconfirm, Card, Row, Col 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LockOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRole, setEditingRole] = useState(null);
  const [searchText, setSearchText] = useState('');
  const { authToken, user } = useAuth();
  
  // Check if user is admin (can create/edit/delete roles)
  const isAdmin = user && (
    user.role === 'admin' ||
    user.role_name === 'admin' ||
    user.role === 'superadmin' ||
    user.role_name === 'superadmin'
  );

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/roles', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/api/roles/permissions', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      message.error('Failed to load permissions');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/api/departments', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to load departments');
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get('/api/regions', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setRegions(response.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      message.error('Failed to load regions');
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      fetchDepartments();
      fetchRegions();
    }
  }, [authToken]);

  const handleSubmit = async (values) => {
    try {
      const roleData = {
        name: values.name,
        description: values.description,
        permissions: values.permissions || [],
        regionIds: values.regionIds || [],
        departmentIds: values.departmentIds || []
      };

      if (editingRole) {
        await axios.put(`/api/roles/${editingRole.id}`, roleData, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        message.success('Role updated successfully');
      } else {
        await axios.post('/api/roles', roleData, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        message.success('Role created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      message.error(error.response?.data?.message || 'Failed to save role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      description: role.description,
      permissions: role.permissions || [],
      // Coerce IDs to numbers to match <Option value={id}> which uses numeric values
      regionIds: (role.region_ids || []).map(id => Number(id)).filter(n => !Number.isNaN(n)),
      departmentIds: (role.department_ids || []).map(id => Number(id)).filter(n => !Number.isNaN(n))
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (roleId) => {
    try {
      await axios.delete(`/api/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      message.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete role';
      message.error(errorMessage);
      
      // Show more specific error messages
      if (error.response?.status === 400) {
        message.warning(errorMessage);
      } else if (error.response?.status === 403) {
        message.error('You do not have permission to delete roles');
      } else if (error.response?.status === 404) {
        message.error('Role not found');
      }
    }
  };

  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchText.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <>
          {text}
          {record.isSystem && <LockOutlined style={{ marginLeft: 8, color: '#999' }} />}
        </>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Regions',
      key: 'regions',
      render: (_, record) => (
        <div style={{ maxWidth: 260 }}>
          {record.regions && record.regions.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {record.regions.map(r => (
                <Tag color="geekblue" key={`region-${r.id}`}>
                  {r.name}
                </Tag>
              ))}
            </div>
          ) : (
            <span style={{ color: '#999' }}>No region scope</span>
          )}
        </div>
      )
    },
    {
      title: 'Departments',
      key: 'departments',
      render: (_, record) => (
        <div style={{ maxWidth: 260 }}>
          {record.departments && record.departments.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {record.departments.map(d => (
                <Tag color="purple" key={`dept-${d.id}`}>
                  {d.name}
                </Tag>
              ))}
            </div>
          ) : (
            <span style={{ color: '#999' }}>No department scope</span>
          )}
        </div>
      )
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => (
        <div style={{ maxWidth: 300 }}>
          {record.permissions && record.permissions.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {record.permissions.map(permission => (
                <Tag color="blue" key={permission}>
                  {permission}
                </Tag>
              ))}
            </div>
          ) : (
            <span style={{ color: '#999' }}>No permissions</span>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          {isAdmin && (
            <>
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => handleEdit(record)}
                disabled={record.isSystem}
                title={record.isSystem ? 'System roles cannot be edited' : 'Edit'}
              />
              <Popconfirm
                title="Delete Role"
                description={`Are you sure you want to delete the role "${record.name}"? This action cannot be undone.`}
                onConfirm={() => handleDelete(record.id)}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                disabled={record.isSystem || record.name === 'admin'}
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />} 
                  disabled={record.isSystem || record.name === 'admin'}
                  title={
                    record.isSystem 
                      ? 'System roles cannot be deleted' 
                      : record.name === 'admin'
                      ? 'Admin role cannot be deleted'
                      : 'Delete role'
                  }
                />
              </Popconfirm>
            </>
          )}
          {!isAdmin && (
            <span style={{ color: '#999', fontSize: '12px' }}>View only</span>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="role-management">
      <Card 
        title="Role Management"
        extra={
          isAdmin && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => {
                setEditingRole(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
            >
              Add Role
            </Button>
          )
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} lg={8}>
            <Input
              placeholder="Search roles..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        <Table 
          columns={columns} 
          dataSource={filteredRoles} 
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingRole(null);
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ permissions: [] }}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[
              { required: true, message: 'Please enter a role name' },
              { min: 3, message: 'Role name must be at least 3 characters' },
              { max: 50, message: 'Role name cannot exceed 50 characters' }
            ]}
          >
            <Input placeholder="Enter role name" disabled={!isAdmin || !!editingRole?.isSystem} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 255, message: 'Description cannot exceed 255 characters' }
            ]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" disabled={!isAdmin} />
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: 'Please select at least one permission' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select permissions"
              style={{ width: '100%' }}
              disabled={!isAdmin || !!editingRole?.isSystem}
            >
              {permissions.map(permission => (
                <Option key={permission.name} value={permission.name}>
                  {permission.name} - {permission.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="regionIds"
                label="Allowed Regions (Role Scope)"
                tooltip="Contracts access for this role is limited to these regions when set. Each user still has a primary region on their profile; it must be one of these (superadmins pick it at user creation)."
              >
                <Select
                  mode="multiple"
                  placeholder="Select regions (optional)"
                  style={{ width: '100%' }}
                  disabled={!isAdmin || !!editingRole?.isSystem}
                  optionFilterProp="children"
                >
                  {regions.map(region => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="departmentIds"
                label="Allowed Departments (Role Scope)"
                tooltip="If you select departments here, users with this role can access contracts in these departments."
              >
                <Select
                  mode="multiple"
                  placeholder="Select departments (optional)"
                  style={{ width: '100%' }}
                  disabled={!isAdmin || !!editingRole?.isSystem}
                  optionFilterProp="children"
                >
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
            <Space>
              {isAdmin && (
                <Button 
                  type="primary" 
                  htmlType="submit"
                  disabled={!!editingRole?.isSystem}
                >
                  {editingRole ? 'Update' : 'Create'} Role
                </Button>
              )}
              <Button 
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setEditingRole(null);
                }}
              >
                {isAdmin ? 'Cancel' : 'Close'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
